/**
 * #9052 B1 / #9057: the KPI row contract and the placeholder identity, split out of
 * declared-exhibit.ts (shrink-on-touch, #9096 review P3). Re-exported from declared-exhibit.ts
 * and the contracts barrel, so every import path stays stable.
 */
import type { AskDeclaredExhibitBody } from './declared-exhibit';

/** One labelled row of a KPI exhibit: the placeholders of the figures it shows, in order. */
export type AskKpiGroup = Readonly<{ label: string; figures: readonly string[] }>;

/**
 * #9057 review: THE placeholder identity, shared by the server and the canvas. A binding and an
 * exhibit figure join on this key and nothing else, so a server-side uniqueness check over keys is
 * exactly the reader's own resolution: a trimmed, non-empty string (JS `trim` also strips Unicode
 * whitespace); case and Unicode form are NOT folded, so values that differ there stay distinct
 * on both sides.
 */
export function askPlaceholderKey(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const key = value.trim();
  return key.length > 0 ? key : null;
}

/**
 * #9052 B1: everything wrong with a KPI exhibit's groups, named; empty is the only publishable
 * state. Groups must partition the figures exactly (each in one row, none missing, none extra),
 * each row non-empty with its own non-blank label, so no figure is dropped or shown twice.
 */
export function askKpiGroupProblems(exhibit: AskDeclaredExhibitBody): readonly string[] {
  if (exhibit.kind !== 'kpi' || exhibit.groups === undefined) return [];
  const figures = exhibit.figures.map((figure) => figure.figure);
  const grouped = exhibit.groups.flatMap((group) => group.figures);
  const labels = exhibit.groups.map((group) => group.label.trim());
  return [
    ...exhibit.groups.filter((group) => group.figures.length === 0).map((group) => `group ${group.label} is empty`),
    ...(labels.some((label) => label.length === 0) ? ['a group has no label'] : []),
    ...(new Set(labels).size !== labels.length ? ['two groups share a label'] : []),
    ...(new Set(grouped).size !== grouped.length ? ['a figure is in two groups'] : []),
    ...figures.filter((figure) => !grouped.includes(figure)).map((figure) => `figure ${figure} is in no group`),
    ...grouped.filter((figure) => !figures.includes(figure)).map((figure) => `group names unknown figure ${figure}`),
  ];
}
